'use client';

import { useEffect, useCallback, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useTranslations } from 'next-intl';
import { MdClose } from 'react-icons/md';
import { Button } from '@/components/ui/button/Button';
import { ButtonSize, ButtonVariant } from '@/components/ui/button/Button.types';
import { Text } from '@/components/ui/text';
import { useFocusTrap } from '@/hooks/useFocusTrap';
import qrcode from 'qrcode-generator';

export interface ViewQRDialogProps {
  open: boolean;
  onClose: () => void;
  description: string;
  cardNumber: string;
}

function generateQRDataURL(data: string): string {
  const qr = qrcode(0, 'M');
  qr.addData(data || '-');
  qr.make();
  return qr.createDataURL(4, 8);
}

export const ViewQRDialog = ({ open, onClose, description, cardNumber }: ViewQRDialogProps) => {
  const t = useTranslations('PermitDetails');
  const tCommon = useTranslations('Common');
  const dialogRef = useRef<HTMLDivElement>(null);
  useFocusTrap(dialogRef, open);

  const qrDataURL = useMemo(() => (open ? generateQRDataURL(cardNumber) : ''), [open, cardNumber]);

  const handleEscape = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    },
    [onClose]
  );

  useEffect(() => {
    if (!open) return;
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [open, handleEscape]);

  if (!open) return null;

  const titleId = 'view-qr-dialog-title';

  const content = (
    <>
      <div
        className="tw:fixed tw:inset-0 tw:z-40 tw:bg-black/40 tw:transition-opacity animate-backdrop-fade-in"
        aria-hidden
        onClick={onClose}
        data-cy="qr-backdrop"
      />
      {/* eslint-disable-next-line jsx-a11y/prefer-tag-over-role -- portal-based dialog */}
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="tw:fixed tw:left-1/2 tw:top-1/2 tw:z-50 tw:w-full tw:max-w-[28rem] tw:-translate-x-1/2 tw:-translate-y-1/2 tw:rounded-8 tw:border tw:border-solid tw:border-gray-light tw:bg-white tw:shadow-lg tw:overflow-hidden"
        data-cy="qr-dialog"
      >
        {/* Header */}
        <div className="tw:flex tw:items-center tw:justify-between tw:gap-nano tw:px-base tw:py-small tw:border-b tw:border-solid tw:border-gray-light">
          <h2 id={titleId} className="tw:font-bold tw:text-font tw:text-20 tw:tracking-[0.3px]">
            {t('showQrTitle')}
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label={tCommon('close')}
            className="tw:flex tw:items-center tw:justify-center tw:rounded tw:p-1 tw:text-gray-dark tw:transition-opacity tw:hover:opacity-80 tw:focus:outline-none tw:focus-visible:ring-2 tw:focus-visible:ring-primary"
            data-cy="qr-close-btn"
          >
            <MdClose className="tw:h-5 tw:w-5" aria-hidden />
          </button>
        </div>

        {/* Body */}
        <div className="tw:bg-gray-lighter tw:p-huge">
          <div className="tw:bg-white tw:rounded-16 tw:px-huge tw:pt-base tw:pb-gargantuan tw:flex tw:flex-col tw:items-center tw:gap-huge">
            {/* Access card label */}
            <div className="tw:w-full tw:flex tw:justify-end">
              <Text size="16" weight="bold" className="tw:text-gray-darker">
                {t('accessCardLabel')}
              </Text>
            </div>

            {/* Description + card number */}
            <div className="tw:flex tw:flex-col tw:items-center tw:gap-base">
              <Text size="24" weight="bold" className="tw:text-font" data-cy="qr-description">
                {description}
              </Text>
              <div className="tw:flex tw:items-center tw:gap-small">
                <div className="tw:h-px tw:w-[53px] tw:bg-gray-light" />
                <div className="tw:bg-gray-lighter tw:rounded-16 tw:px-base tw:py-micro">
                  <Text size="14" weight="bold" className="tw:text-font" data-cy="qr-card-number">
                    {cardNumber}
                  </Text>
                </div>
                <div className="tw:h-px tw:w-[53px] tw:bg-gray-light" />
              </div>
            </div>

            {/* QR Code */}
            <div className="tw:flex tw:flex-col tw:items-center tw:gap-huge">
              {/* eslint-disable-next-line @next/next/no-img-element -- data URL, not optimizable */}
              <img
                src={qrDataURL}
                alt="QR Code"
                className="tw:w-40 tw:h-40"
                data-cy="qr-code-image"
              />
              <Text size="14" className="tw:text-gray-dark">
                {t('scanAtGarage')}
              </Text>
            </div>
          </div>

          {/* Done button */}
          <div className="tw:mt-huge">
            <Button
              size={ButtonSize.large}
              variant={ButtonVariant.primary}
              onClick={onClose}
              className="tw:w-full"
              data-cy="qr-done-btn"
            >
              {tCommon('done')}
            </Button>
          </div>
        </div>
      </div>
    </>
  );

  return createPortal(content, document.body);
};
